import React, { useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { ArticleLoader } from '../../home/TasksPageLoader';
import CheckIcon from '@mui/icons-material/Check';

interface RecommendedArticleProps {
  direction?: 'row' | 'column';
  place?: 'library' | 'menteeTask' | 'help' | 'mentorHome';
  data?: Article[];
  isLoading?: boolean;
  handleArticleClick?: (id: string, location: string) => void;
  viewedArticles?: Record<string, boolean>;
}

const RecommendedArticle: React.FC<RecommendedArticleProps> = ({
  direction = 'row',
  place = 'library',
  data = [],
  isLoading,
  handleArticleClick = () => {},
  viewedArticles = {}
}) => {
  const articles = data;

  const [locallyReadArticles, setLocallyReadArticles] = useState<Record<string, boolean>>({});
  const [checkedArticles, setCheckedArticles] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(articles.map((a) => [a.id, false]))
  );

  const getIsRead = (article: Article) =>
    locallyReadArticles[article.id] || article.isRead || viewedArticles[article.id];

  const handleArticleClickWrapper = (id: string, location: string) => {
    setLocallyReadArticles((prev) => ({ ...prev, [id]: true }));
    handleArticleClick(id, location);
  };

  const getTitle = () => {
    if (place === 'menteeTask') return 'Lesson Materials';
    return 'Recommended Content';
  };

  const getPlaceholder = () => {
    if (place === 'menteeTask') return 'There are no Lesson Materials to review for this Lesson';
    if (place === 'mentorHome') return 'No curated articles available.';
    return 'No similar data found.';
  };

  return isLoading ? (
    <ArticleLoader />
  ) : (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        fontFamily: "'Open Sans', sans-serif"
      }}
    >
      {/* Title */}
      {/* <Typography
        sx={{
          color: '#FDFDFD',
          fontSize: '16px',
          fontWeight: 700,
          fontFamily: 'Open Sans',
          lineHeight: '16px'
        }}
      >
        {getTitle()}
      </Typography> */}

      {/* Content */}
      {articles.length === 0 ? (
        <Typography
          sx={{
            width: '100%',
            textAlign: 'left',
            color: '#B6B6B6',
            fontSize: '14px',
            fontFamily: 'Open Sans !important'
          }}
        >
          {getPlaceholder()}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '30px',
            overflowX: direction === 'row' ? 'auto' : 'visible',
            pb: direction === 'row' ? '5px' : 0
          }}
        >
          {articles.map((article) => {
            const isRead = getIsRead(article);

            return (
              <Box
                key={article.id}
                onClick={() => handleArticleClickWrapper(article.id, article.location)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '163px'
                }}
              >
                {/* Thumbnail */}
                <Box
                  component="img"
                  src={article.coverImageUrl}
                  alt={article.articleName}
                  sx={{
                    width: '163px',
                    height: '100px',
                    borderRadius: '10px',
                    objectFit: 'cover'
                  }}
                />

                {/* Row under image */}
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '10px'
                  }}
                >
                  {/* Status Icon (GREEN CIRCLE ✔️) */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {isRead && (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: '#00972D',
                          border: '2px solid #FDFDFD',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckIcon
                          sx={{
                            fontSize: 12,
                            color: '#FDFDFD',
                            position: 'relative',
                            top: '-1px'
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* TEXT */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1
                    }}
                  >
                    <Tooltip title={article.articleName}>
              <Typography
                sx={{
                  color: '#FDFDFD',
                  fontSize: '12px',
                  fontFamily: 'Open Sans',
                  fontWeight: 700,
                  lineHeight: '14px',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                        {article.articleName}
                      </Typography>
                    </Tooltip>

                    <Typography
                      sx={{
                        color: '#868686',
                        fontSize: '10px',
                        fontFamily: 'Open Sans',
                        fontWeight: 400
                      }}
                    >
                      {article.readTime}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default RecommendedArticle;
